// import { AccountType } from "shared/types/accounts";
import { skedApi } from "./_skedApi";
import { GeneratedSkedApiClient } from "./generated-client";
import type { paths } from "./generated/sked-api-openapi-types";
import pick from "lodash/pick";

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();

type GetGroupsOperation = paths["/account-groups"]["get"];
type AddGroupsOperation = paths["/account-groups"]["post"];
type UpdateGroupsOperation = paths["/account-groups/{groupId}"]["patch"];
type GetGroupOperation = paths["/account-groups/{groupId}"]["get"];

type GetGroupsResponse =
  GetGroupsOperation["responses"][200]["content"]["application/json"];
type GetGroupResponse =
  GetGroupOperation["responses"][200]["content"]["application/json"];
type AddGroupsBody = NonNullable<
  AddGroupsOperation["requestBody"]
>["content"]["application/json"];
type AddGroupsResponse =
  AddGroupsOperation["responses"][200]["content"]["application/json"];
type UpdateGroupsBody = NonNullable<
  UpdateGroupsOperation["requestBody"]
>["content"]["application/json"];
type UpdateGroupsResponse =
  UpdateGroupsOperation["responses"][200]["content"]["application/json"];
type AccountGroupInput = Partial<AddGroupsBody & UpdateGroupsBody> &
  Record<string, unknown>;

type GetGroupsResult = {
  data: GetGroupsResponse;
};

let inFlightGetGroupsRequest: Promise<GetGroupsResult> | null = null;

export function getGroups(): Promise<GetGroupsResult> {
  if (inFlightGetGroupsRequest) {
    return inFlightGetGroupsRequest;
  }

  const request = (async () => {
    const result = await generatedSkedApiClient.client.GET("/account-groups");

    return {
      data: (await generatedSkedApiClient.unwrap(result)) as GetGroupsResponse,
    };
  })().finally(() => {
    if (inFlightGetGroupsRequest === request) {
      inFlightGetGroupsRequest = null;
    }
  });

  inFlightGetGroupsRequest = request;
  return request;
}

/**
 * Fetches a single group by id. Used only on the deep-link failure path
 * (SKE-4213 FR5): when a `/dashboard/groups/:groupId` link does not resolve
 * against the already-scoped list, this distinguishes "you cannot see it" from
 * "it no longer exists" so support gets accurate copy. The route runs
 * `validateGroupAccess` server-side, so the 403/404 split is real.
 */
export async function getGroup(groupId: string): Promise<GetGroupResponse> {
  const result = await generatedSkedApiClient.client.GET(
    "/account-groups/{groupId}",
    { params: { path: { groupId } } }
  );

  return (await generatedSkedApiClient.unwrap(result)) as GetGroupResponse;
}

export async function getGroupKnockToken(groupId: string) {
  return skedApi.get<{ knockUserToken: string }>(
    `/knock/tokens/groups/${groupId}`
  );
}

export async function addGroups(accountGroupData: AccountGroupInput) {
  const result = await generatedSkedApiClient.client.POST("/account-groups", {
    body: pick(accountGroupData, [
      "name",
      "description",
      "adAccountId",
      "ga4PropertyId",
      "googleProfileId",
      "ga4CustomEventNames",
      "accountIds",
      "language",
      "applyLanguageToAllAction",
    ]) as AddGroupsBody,
  });

  return {
    data: (await generatedSkedApiClient.unwrap(result)) as AddGroupsResponse,
  };
}

export async function updateGroups(
  id: string,
  accountGroupData: AccountGroupInput
) {
  const result = await generatedSkedApiClient.client.PATCH(
    "/account-groups/{groupId}",
    {
      params: {
        path: {
          groupId: id,
        },
      },
      body: pick(accountGroupData, [
        "name",
        "description",
        "adAccountId",
        "ga4PropertyId",
        "googleProfileId",
        // Present in the POST body but historically missing here, so edits to
        // the custom-conversion list were silently dropped on update.
        "ga4CustomEventNames",
        "accountIds",
        "language",
        "applyLanguageToAllAction",
      ]) as UpdateGroupsBody,
    }
  );

  return {
    data: (await generatedSkedApiClient.unwrap(result)) as UpdateGroupsResponse,
  };
}

export async function deleteGroups(id: string) {
  const result = await generatedSkedApiClient.client.DELETE(
    "/account-groups/{groupId}",
    {
      params: {
        path: {
          groupId: id,
        },
      },
    }
  );

  if (!result.response.ok) {
    await generatedSkedApiClient.unwrap(result);
  }

  return {
    data: result.data,
  };
}
